import { useCallback, useEffect, useMemo, useRef, useState, type WheelEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import { ArrowLeft, Check, ChevronsUpDown, Copy, FileDown, FolderPlus, GripVertical, Loader2, Plus, Search, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { formatarMoedaOrcamento, orcamentosCarregadoresApi } from '@/lib/orcamentosCarregadoresApi';
import {
  rotuloOrigemItem,
  totalGrupoOrcamento,
  totalItemOrcamento,
  totalOrcamento,
  type GrupoDespesaOrcamento,
  type ItemOrcamentoCarregador,
  type OrcamentoCarregador,
  type OrcamentoCarregadorResumo,
  type ProdutoOrcamento,
} from '@/types/orcamentosCarregadores';
import { formatBrazilianCurrencyValue, formatCurrencyInput, parseBrazilianCurrency } from '@/utils/formatters';
import { cn } from '@/lib/utils';

const UNIDADES_INTEIRAS = ['UN', 'UND', 'UNID', 'PC', 'PCA', 'PCS', 'CX', 'KIT', 'JG', 'PAR', 'CJ'];

interface FormOrcamento {
  nome_projeto: string;
  cliente: string;
  local_obra: string;
  observacoes: string;
  grupos: GrupoDespesaOrcamento[];
  itens: ItemOrcamentoCarregador[];
}

type SaidaPendente = { tipo: 'lista' } | { tipo: 'rota'; href: string };

const FORM_VAZIO: FormOrcamento = {
  nome_projeto: '',
  cliente: '',
  local_obra: '',
  observacoes: '',
  grupos: [],
  itens: [],
};

function formVazio(): FormOrcamento {
  return { ...FORM_VAZIO, grupos: [], itens: [] };
}

function formularioDeOrcamento(orcamento: Pick<OrcamentoCarregador, 'nome_projeto' | 'cliente' | 'local_obra' | 'observacoes' | 'grupos' | 'itens'>): FormOrcamento {
  const grupos = (Array.isArray(orcamento.grupos) ? orcamento.grupos : [])
    .filter((grupo) => grupo && typeof grupo.id === 'string')
    .map((grupo) => ({ id: grupo.id, nome: grupo.nome || '' }));
  const ids = new Set(grupos.map((grupo) => grupo.id));
  let itens = (Array.isArray(orcamento.itens) ? orcamento.itens : []).map((item) => ({
    ...item,
    grupo_id: item.grupo_id && ids.has(item.grupo_id) ? item.grupo_id : null,
  }));

  if (grupos.length === 0 && itens.length > 0) {
    const id = crypto.randomUUID();
    grupos.push({ id, nome: 'Despesas gerais' });
    itens = itens.map((item) => ({ ...item, grupo_id: id }));
  } else if (grupos.length > 0) {
    const padrao = grupos[0].id;
    itens = itens.map((item) => (item.grupo_id ? item : { ...item, grupo_id: padrao }));
  }

  return {
    nome_projeto: orcamento.nome_projeto,
    cliente: orcamento.cliente || '',
    local_obra: orcamento.local_obra || '',
    observacoes: orcamento.observacoes || '',
    grupos,
    itens,
  };
}

function nomeCopia(nome: string) {
  const sufixo = ' (cópia)';
  const base = nome.trim().slice(0, 200 - sufixo.length);
  return `${base}${sufixo}`;
}

function formularioCopia(origem: FormOrcamento): FormOrcamento {
  const mapaGrupos = new Map<string, string>();
  const grupos = origem.grupos.map((grupo) => {
    const id = crypto.randomUUID();
    mapaGrupos.set(grupo.id, id);
    return { id, nome: grupo.nome };
  });
  return {
    nome_projeto: nomeCopia(origem.nome_projeto),
    cliente: origem.cliente,
    local_obra: origem.local_obra,
    observacoes: origem.observacoes,
    grupos,
    itens: origem.itens.map((item) => ({
      ...item,
      id: crypto.randomUUID(),
      grupo_id: item.grupo_id ? mapaGrupos.get(item.grupo_id) ?? null : null,
    })),
  };
}

function caminhoAtual() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function caminhoHistorico(url: string | URL | null | undefined) {
  if (url == null) return null;
  try {
    const resolvido = new URL(String(url), window.location.href);
    if (resolvido.origin !== window.location.origin) return null;
    return `${resolvido.pathname}${resolvido.search}${resolvido.hash}`;
  } catch {
    return null;
  }
}

function formatarData(iso: string) {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return iso;
  return data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function unidadeInteira(unidade: string) {
  return UNIDADES_INTEIRAS.includes(unidade.trim().toUpperCase());
}

function bloquearRolagem(e: WheelEvent<HTMLInputElement>) {
  e.currentTarget.blur();
}

function novoItem(parcial: Omit<ItemOrcamentoCarregador, 'id'>): ItemOrcamentoCarregador {
  return { ...parcial, incluir_no_pdf: parcial.incluir_no_pdf !== false, id: crypto.randomUUID() };
}

function itemEntraNoPdf(item: ItemOrcamentoCarregador) {
  return item.incluir_no_pdf !== false;
}

function itemCombinaBusca(item: ItemOrcamentoCarregador, termo: string) {
  if (!termo) return true;
  return [item.codigo, item.descricao].some((valor) => valor.toLowerCase().includes(termo));
}

function itensDoGrupo(itens: ItemOrcamentoCarregador[], grupoId: string) {
  return itens.filter((item) => item.grupo_id === grupoId);
}

function reordenarGrupo(
  itens: ItemOrcamentoCarregador[],
  grupoId: string,
  origem: number,
  destino: number,
  termo: string,
) {
  const visiveis = itensDoGrupo(itens, grupoId).filter((item) => itemCombinaBusca(item, termo));
  if (origem === destino || origem < 0 || destino < 0 || origem >= visiveis.length || destino >= visiveis.length) {
    return itens;
  }
  const ordem = visiveis.map((item) => item.id);
  const [movido] = ordem.splice(origem, 1);
  ordem.splice(destino, 0, movido);
  const porId = new Map(itensDoGrupo(itens, grupoId).map((item) => [item.id, item]));
  const visiveisIds = new Set(ordem);
  let cursor = 0;
  const reordenados = itensDoGrupo(itens, grupoId).map((item) => {
    if (!visiveisIds.has(item.id)) return item;
    const id = ordem[cursor];
    cursor += 1;
    return porId.get(id) ?? item;
  });
  let indice = 0;
  return itens.map((item) => {
    if (item.grupo_id !== grupoId) return item;
    const proximo = reordenados[indice];
    indice += 1;
    return proximo ?? item;
  });
}

function moverItemEntreGrupos(
  itens: ItemOrcamentoCarregador[],
  origemId: string,
  destinoId: string,
  origem: number,
  destino: number,
  termo: string,
) {
  const origemVisiveis = itensDoGrupo(itens, origemId).filter((item) => itemCombinaBusca(item, termo));
  const item = origemVisiveis[origem];
  if (!item) return itens;
  const restante = itens.filter((atual) => atual.id !== item.id);
  const movido = { ...item, grupo_id: destinoId };
  const destinoVisiveis = itensDoGrupo(restante, destinoId).filter((atual) => itemCombinaBusca(atual, termo));
  const ancora = destinoVisiveis[destino];
  if (!ancora) {
    let ultimoIndice = -1;
    restante.forEach((atual, indice) => {
      if (atual.grupo_id === destinoId) ultimoIndice = indice;
    });
    if (ultimoIndice < 0) return [...restante, movido];
    const copia = [...restante];
    copia.splice(ultimoIndice + 1, 0, movido);
    return copia;
  }
  const indice = restante.findIndex((atual) => atual.id === ancora.id);
  const copia = [...restante];
  copia.splice(indice, 0, movido);
  return copia;
}

function SeletorProduto({
  produtos,
  value,
  onChange,
  id,
}: {
  produtos: ProdutoOrcamento[];
  value: string;
  onChange: (id: string) => void;
  id: string;
}) {
  const [aberto, setAberto] = useState(false);
  const selecionado = produtos.find((produto) => produto.id === value);

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={aberto}
          disabled={produtos.length === 0}
          className="w-full sm:flex-1 justify-between font-normal"
        >
          <span className="truncate text-left">
            {selecionado
              ? `${selecionado.codigo} — ${selecionado.descricao}`
              : produtos.length
                ? 'Buscar produto cadastrado'
                : 'Nenhum produto cadastrado em Notas Fiscais'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar por código ou descrição" />
          <CommandList>
            <CommandEmpty>Nenhum material encontrado.</CommandEmpty>
            <CommandGroup>
              {produtos.map((produto) => (
                <CommandItem
                  key={produto.id}
                  value={`${produto.codigo} ${produto.descricao}`}
                  onSelect={() => {
                    onChange(produto.id);
                    setAberto(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4 shrink-0', value === produto.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">
                    <span className="font-medium">{produto.codigo}</span>
                    <span className="text-muted-foreground"> — {produto.descricao}</span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CelulaMoeda({ value, onChange }: { value: number; onChange: (valor: number) => void }) {
  const seguro = Number.isFinite(value) ? value : 0;
  return (
    <Input
      className="h-8 text-right"
      inputMode="numeric"
      autoComplete="off"
      placeholder="0,00"
      value={seguro === 0 ? '' : formatBrazilianCurrencyValue(seguro)}
      onChange={(e) => onChange(parseBrazilianCurrency(formatCurrencyInput(e.target.value)))}
    />
  );
}

function SecaoGrupo({
  grupo,
  itens,
  produtos,
  produtoSelecionado,
  termo,
  onProduto,
  onAdicionarProduto,
  onAdicionarAvulso,
  onRenomear,
  onExcluir,
  onAtualizarItem,
  onRemoverItem,
  onMarcarPdf,
}: {
  grupo: GrupoDespesaOrcamento;
  itens: ItemOrcamentoCarregador[];
  produtos: ProdutoOrcamento[];
  produtoSelecionado: string;
  termo: string;
  onProduto: (id: string) => void;
  onAdicionarProduto: () => void;
  onAdicionarAvulso: () => void;
  onRenomear: (nome: string) => void;
  onExcluir: () => void;
  onAtualizarItem: (id: string, campos: Partial<ItemOrcamentoCarregador>) => void;
  onRemoverItem: (id: string) => void;
  onMarcarPdf: (marcado: boolean) => void;
}) {
  const visiveis = itens.filter((item) => itemCombinaBusca(item, termo));
  const incluidos = itens.filter(itemEntraNoPdf).length;
  const marcacao =
    itens.length === 0 || incluidos === 0 ? false : incluidos === itens.length ? true : 'indeterminate';
  const subtotal = totalGrupoOrcamento(itens, grupo.id);
  const nomeGrupo = grupo.nome.trim() || 'grupo sem nome';

  return (
    <section className="rounded-md border" aria-label={`Grupo ${nomeGrupo}`}>
      <div className="flex flex-col gap-3 border-b bg-muted/30 p-3 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1 space-y-1">
          <Label htmlFor={`grupo-${grupo.id}`}>Grupo de despesa</Label>
          <Input
            id={`grupo-${grupo.id}`}
            value={grupo.nome}
            placeholder="Ex.: Cabeamentos, eletrocalhas, detecção de incêndio"
            onChange={(e) => onRenomear(e.target.value)}
          />
        </div>
        <Button type="button" variant="outline" onClick={onExcluir}>
          <Trash2 className="mr-1 h-4 w-4" /> Excluir grupo
        </Button>
      </div>
      <div className="space-y-3 p-3">
        <div className="flex flex-col gap-2 sm:flex-row">
          <SeletorProduto
            id={`produto-${grupo.id}`}
            produtos={produtos}
            value={produtoSelecionado}
            onChange={onProduto}
          />
          <Button type="button" onClick={onAdicionarProduto} disabled={!produtoSelecionado}>
            <Plus className="mr-1 h-4 w-4" /> Adicionar
          </Button>
          <Button type="button" variant="outline" onClick={onAdicionarAvulso}>
            <Plus className="mr-1 h-4 w-4" /> Item avulso
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead className="w-10">#</TableHead>
                <TableHead className="min-w-[110px]">Código</TableHead>
                <TableHead className="min-w-[240px]">Descrição</TableHead>
                <TableHead className="min-w-[80px]">Un.</TableHead>
                <TableHead className="min-w-[110px]">Qtd.</TableHead>
                <TableHead className="min-w-[140px]">Valor un.</TableHead>
                <TableHead className="w-[120px] text-right">Total</TableHead>
                <TableHead className="w-16 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <span>PDF</span>
                    <Checkbox
                      checked={marcacao}
                      onCheckedChange={(marcado) => onMarcarPdf(marcado === true)}
                      aria-label={`Incluir os itens de ${nomeGrupo} no PDF`}
                      disabled={itens.length === 0}
                    />
                  </div>
                </TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <Droppable droppableId={grupo.id}>
              {(listaArraste) => (
                <TableBody ref={listaArraste.innerRef} {...listaArraste.droppableProps}>
                  {visiveis.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="py-6 text-center text-sm text-muted-foreground">
                        {termo
                          ? 'Nenhum material deste grupo para essa busca.'
                          : 'Nenhum item neste grupo. Adicione um produto ou arraste um item para cá.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    visiveis.map((item, indice) => (
                      <Draggable key={item.id} draggableId={item.id} index={indice}>
                        {(arraste, snapshotArraste) => (
                          <TableRow
                            ref={arraste.innerRef}
                            {...arraste.draggableProps}
                            className={snapshotArraste.isDragging ? 'bg-muted' : undefined}
                          >
                            <TableCell className="w-10 px-2">
                              <button
                                type="button"
                                className="flex h-8 w-8 cursor-grab items-center justify-center rounded-md text-muted-foreground hover:bg-muted active:cursor-grabbing"
                                aria-label={`Arrastar ${item.descricao || 'item'} para reordenar`}
                                {...arraste.dragHandleProps}
                              >
                                <GripVertical className="h-4 w-4" />
                              </button>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{indice + 1}</TableCell>
                            <TableCell>
                              {item.origem === 'produto' ? (
                                <span className="text-sm">{item.codigo}</span>
                              ) : (
                                <Input
                                  className="h-8"
                                  value={item.codigo}
                                  onChange={(e) => onAtualizarItem(item.id, { codigo: e.target.value })}
                                />
                              )}
                              <Badge variant="outline" className="mt-1">
                                {rotuloOrigemItem(item.origem)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {item.origem === 'produto' ? (
                                <span className="text-sm">{item.descricao}</span>
                              ) : (
                                <Input
                                  className="h-8"
                                  value={item.descricao}
                                  placeholder="Descrição"
                                  onChange={(e) => onAtualizarItem(item.id, { descricao: e.target.value })}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {item.origem === 'produto' ? (
                                <span className="text-sm">{item.unidade}</span>
                              ) : (
                                <Input
                                  className="h-8"
                                  value={item.unidade}
                                  onChange={(e) => onAtualizarItem(item.id, { unidade: e.target.value.toUpperCase() })}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Input
                                className="h-8"
                                type="number"
                                min={0}
                                step={unidadeInteira(item.unidade) ? 1 : 0.01}
                                value={item.quantidade}
                                onWheel={bloquearRolagem}
                                onChange={(e) => {
                                  const valor = Number(e.target.value);
                                  if (!Number.isFinite(valor)) return;
                                  onAtualizarItem(item.id, {
                                    quantidade: unidadeInteira(item.unidade) ? Math.round(valor) : valor,
                                  });
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <CelulaMoeda
                                value={item.valor_unitario}
                                onChange={(valor) => onAtualizarItem(item.id, { valor_unitario: valor })}
                              />
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatarMoedaOrcamento(totalItemOrcamento(item))}
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={itemEntraNoPdf(item)}
                                onCheckedChange={(marcado) =>
                                  onAtualizarItem(item.id, { incluir_no_pdf: marcado === true })
                                }
                                aria-label={`Incluir ${item.descricao || 'item'} no PDF`}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Remover item"
                                onClick={() => onRemoverItem(item.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))
                  )}
                  <TableRow className="h-0 border-0 hover:bg-transparent">
                    <TableCell colSpan={10} className="h-0 border-0 p-0">
                      {listaArraste.placeholder}
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Droppable>
          </Table>
        </div>
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-muted-foreground">
            {itens.length} {itens.length === 1 ? 'item' : 'itens'}
          </span>
          <span className="font-medium">Subtotal {formatarMoedaOrcamento(subtotal)}</span>
        </div>
      </div>
    </section>
  );
}

export default function OrcamentosCarregadores() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [lista, setLista] = useState<OrcamentoCarregadorResumo[]>([]);
  const [produtos, setProdutos] = useState<ProdutoOrcamento[]>([]);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [carregandoEditor, setCarregandoEditor] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [buscaLista, setBuscaLista] = useState('');
  const [buscaItens, setBuscaItens] = useState('');
  const [produtoPorGrupo, setProdutoPorGrupo] = useState<Record<string, string>>({});
  const [gerandoPdfId, setGerandoPdfId] = useState<string | null>(null);
  const [modo, setModo] = useState<'lista' | 'editor'>('lista');
  const [orcamentoId, setOrcamentoId] = useState<string | null>(null);
  const [form, setForm] = useState<FormOrcamento>(formVazio);
  const [snapshot, setSnapshot] = useState('');
  const [excluirId, setExcluirId] = useState<string | null>(null);
  const [excluirGrupoId, setExcluirGrupoId] = useState<string | null>(null);
  const [saidaPendente, setSaidaPendente] = useState<SaidaPendente | null>(null);
  const [excluindo, setExcluindo] = useState(false);
  const pedidoEditor = useRef(0);
  const sujoRef = useRef(false);
  const bypassHistorico = useRef(false);
  const hrefEditor = useRef('');
  const estadoEditor = useRef<unknown>(null);
  const saidaAbertaRef = useRef(false);
  const saidaPendenteRef = useRef<SaidaPendente | null>(null);

  const carregarLista = useCallback(async () => {
    setCarregandoLista(true);
    try {
      setLista(await orcamentosCarregadoresApi.listar());
    } catch (e) {
      toast({
        title: 'Erro ao carregar orçamentos',
        description: e instanceof Error ? e.message : 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setCarregandoLista(false);
    }
  }, [toast]);

  const carregarCatalogo = useCallback(async () => {
    try {
      const catalogo = await orcamentosCarregadoresApi.catalogo();
      setProdutos(catalogo.produtos || []);
    } catch (e) {
      toast({
        title: 'Erro ao carregar produtos',
        description: e instanceof Error ? e.message : 'Tente novamente.',
        variant: 'destructive',
      });
    }
  }, [toast]);

  useEffect(() => {
    carregarLista();
    carregarCatalogo();
  }, [carregarLista, carregarCatalogo]);

  const listaFiltrada = useMemo(() => {
    const termo = buscaLista.trim().toLowerCase();
    if (!termo) return lista;
    return lista.filter((item) =>
      [item.nome_projeto, item.cliente, item.local_obra].some((valor) =>
        String(valor || '').toLowerCase().includes(termo)
      )
    );
  }, [buscaLista, lista]);

  const produtosOrdenados = useMemo(
    () => [...produtos].sort((a, b) => a.descricao.localeCompare(b.descricao, 'pt-BR')),
    [produtos]
  );

  const termoItens = buscaItens.trim().toLowerCase();
  const incluidosNoPdf = form.itens.filter(itemEntraNoPdf).length;
  const total = totalOrcamento(form.itens);
  const sujo = snapshot !== '' && snapshot !== JSON.stringify(form);
  sujoRef.current = modo === 'editor' && sujo;
  saidaAbertaRef.current = saidaPendente !== null;
  saidaPendenteRef.current = saidaPendente;

  useEffect(() => {
    if (modo !== 'editor') return undefined;
    hrefEditor.current = caminhoAtual();
    estadoEditor.current = window.history.state;

    const pushOriginal = window.history.pushState.bind(window.history);
    const replaceOriginal = window.history.replaceState.bind(window.history);
    const interceptar =
      (original: History['pushState']): History['pushState'] =>
      (data, unused, url) => {
        if (!bypassHistorico.current && sujoRef.current) {
          const destino = caminhoHistorico(url);
          if (destino && destino !== caminhoAtual()) {
            if (!saidaAbertaRef.current) setSaidaPendente({ tipo: 'rota', href: destino });
            return;
          }
        }
        return original(data, unused, url);
      };

    window.history.pushState = interceptar(pushOriginal);
    window.history.replaceState = interceptar(replaceOriginal);

    const aoPop = () => {
      if (!sujoRef.current || !hrefEditor.current) return;
      const destino = caminhoAtual();
      if (destino === hrefEditor.current) return;
      bypassHistorico.current = true;
      window.history.pushState(estadoEditor.current, '', hrefEditor.current);
      bypassHistorico.current = false;
      if (!saidaAbertaRef.current) setSaidaPendente({ tipo: 'rota', href: destino });
    };

    const aoFechar = (event: BeforeUnloadEvent) => {
      if (!sujoRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('popstate', aoPop, true);
    window.addEventListener('beforeunload', aoFechar);
    return () => {
      window.history.pushState = pushOriginal;
      window.history.replaceState = replaceOriginal;
      window.removeEventListener('popstate', aoPop, true);
      window.removeEventListener('beforeunload', aoFechar);
    };
  }, [modo]);

  const abrirNovo = () => {
    pedidoEditor.current += 1;
    const inicial = formVazio();
    setOrcamentoId(null);
    setForm(inicial);
    setSnapshot(JSON.stringify(inicial));
    setProdutoPorGrupo({});
    setBuscaItens('');
    setCarregandoEditor(false);
    setModo('editor');
    carregarCatalogo();
  };

  const abrirCopia = (origem: FormOrcamento) => {
    const copia = formularioCopia(origem);
    setOrcamentoId(null);
    setForm(copia);
    setSnapshot(JSON.stringify(origem));
    setProdutoPorGrupo({});
    setBuscaItens('');
    setCarregandoEditor(false);
    setModo('editor');
    toast({
      title: 'Cópia pronta para editar',
      description: 'O orçamento original não foi alterado. Salve para gravar o novo.',
    });
  };

  const copiarAtual = () => {
    pedidoEditor.current += 1;
    abrirCopia(form);
  };

  const copiarExistente = async (id: string) => {
    const pedido = ++pedidoEditor.current;
    setCarregandoEditor(true);
    setModo('editor');
    setOrcamentoId(null);
    try {
      const orcamento = await orcamentosCarregadoresApi.obter(id);
      if (pedido !== pedidoEditor.current) return;
      const carregado = formularioDeOrcamento(orcamento);
      abrirCopia(carregado);
      carregarCatalogo();
    } catch (e) {
      if (pedido !== pedidoEditor.current) return;
      setModo('lista');
      toast({
        title: 'Erro ao copiar orçamento',
        description: e instanceof Error ? e.message : 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      if (pedido === pedidoEditor.current) setCarregandoEditor(false);
    }
  };

  const abrirExistente = async (id: string) => {
    const pedido = ++pedidoEditor.current;
    setCarregandoEditor(true);
    setModo('editor');
    setOrcamentoId(id);
    try {
      const orcamento = await orcamentosCarregadoresApi.obter(id);
      if (pedido !== pedidoEditor.current) return;
      const carregado = formularioDeOrcamento(orcamento);
      setForm(carregado);
      setSnapshot(JSON.stringify(carregado));
      setProdutoPorGrupo({});
      setBuscaItens('');
      carregarCatalogo();
    } catch (e) {
      if (pedido !== pedidoEditor.current) return;
      setModo('lista');
      toast({
        title: 'Erro ao abrir orçamento',
        description: e instanceof Error ? e.message : 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      if (pedido === pedidoEditor.current) setCarregandoEditor(false);
    }
  };

  const adicionarGrupo = () => {
    const id = crypto.randomUUID();
    setForm((atual) => ({ ...atual, grupos: [...atual.grupos, { id, nome: '' }] }));
    window.setTimeout(() => document.getElementById(`grupo-${id}`)?.focus(), 0);
  };

  const renomearGrupo = (id: string, nome: string) => {
    setForm((atual) => ({
      ...atual,
      grupos: atual.grupos.map((grupo) => (grupo.id === id ? { ...grupo, nome } : grupo)),
    }));
  };

  const pedirExclusaoGrupo = (id: string) => {
    const grupo = form.grupos.find((atual) => atual.id === id);
    if (!grupo) return;
    const possuiItens = form.itens.some((item) => item.grupo_id === id);
    if (!possuiItens) {
      setForm((atual) => ({ ...atual, grupos: atual.grupos.filter((item) => item.id !== id) }));
      return;
    }
    setExcluirGrupoId(id);
  };

  const confirmarExclusaoGrupo = () => {
    if (!excluirGrupoId) return;
    const id = excluirGrupoId;
    setForm((atual) => ({
      ...atual,
      grupos: atual.grupos.filter((grupo) => grupo.id !== id),
      itens: atual.itens.filter((item) => item.grupo_id !== id),
    }));
    setExcluirGrupoId(null);
  };

  const adicionarProdutoNoGrupo = (grupoId: string) => {
    const produtoId = produtoPorGrupo[grupoId];
    const produto = produtos.find((item) => item.id === produtoId);
    if (!produto) return;
    setForm((atual) => {
      const existente = atual.itens.find((item) => item.produto_id === produto.id && item.grupo_id === grupoId);
      if (existente) {
        return {
          ...atual,
          itens: atual.itens.map((item) =>
            item.id === existente.id
              ? {
                  ...item,
                  codigo: produto.codigo,
                  descricao: produto.descricao,
                  unidade: produto.unidade || item.unidade,
                  quantidade: item.quantidade + 1,
                }
              : item
          ),
        };
      }
      return {
        ...atual,
        itens: [
          ...atual.itens,
          novoItem({
            origem: 'produto',
            produto_id: produto.id,
            modelo_carregador_id: null,
            grupo_id: grupoId,
            codigo: produto.codigo,
            descricao: produto.descricao,
            unidade: produto.unidade || 'UN',
            quantidade: 1,
            valor_unitario: Number(produto.preco_custo) || 0,
            incluir_no_pdf: true,
          }),
        ],
      };
    });
    setProdutoPorGrupo((atual) => ({ ...atual, [grupoId]: '' }));
  };

  const adicionarAvulsoNoGrupo = (grupoId: string) => {
    setForm((atual) => ({
      ...atual,
      itens: [
        ...atual.itens,
        novoItem({
          origem: 'avulso',
          produto_id: null,
          modelo_carregador_id: null,
          grupo_id: grupoId,
          codigo: '',
          descricao: '',
          unidade: 'UN',
          quantidade: 1,
          valor_unitario: 0,
          incluir_no_pdf: true,
        }),
      ],
    }));
  };

  const atualizarItem = (id: string, campos: Partial<ItemOrcamentoCarregador>) => {
    setForm((atual) => ({
      ...atual,
      itens: atual.itens.map((item) => (item.id === id ? { ...item, ...campos } : item)),
    }));
  };

  const removerItem = (id: string) => {
    setForm((atual) => ({ ...atual, itens: atual.itens.filter((item) => item.id !== id) }));
  };

  const marcarPdfDoGrupo = (grupoId: string, marcado: boolean) => {
    setForm((atual) => ({
      ...atual,
      itens: atual.itens.map((item) => (item.grupo_id === grupoId ? { ...item, incluir_no_pdf: marcado } : item)),
    }));
  };

  const aoSoltarItem = (resultado: DropResult) => {
    const destino = resultado.destination;
    if (!destino) return;
    const origem = resultado.source;
    if (origem.droppableId === destino.droppableId && origem.index === destino.index) return;
    setForm((atual) => ({
      ...atual,
      itens:
        origem.droppableId === destino.droppableId
          ? reordenarGrupo(atual.itens, origem.droppableId, origem.index, destino.index, termoItens)
          : moverItemEntreGrupos(
              atual.itens,
              origem.droppableId,
              destino.droppableId,
              origem.index,
              destino.index,
              termoItens
            ),
    }));
  };

  const sairParaLista = () => {
    pedidoEditor.current += 1;
    setModo('lista');
    setOrcamentoId(null);
    carregarLista();
  };

  const voltar = () => {
    if (sujo) {
      setSaidaPendente({ tipo: 'lista' });
      return;
    }
    sairParaLista();
  };

  const confirmarSaida = () => {
    const pendente = saidaPendenteRef.current;
    if (!pendente) return;
    sujoRef.current = false;
    setSaidaPendente(null);
    setSnapshot('');
    if (pendente.tipo === 'lista') {
      sairParaLista();
      return;
    }
    bypassHistorico.current = true;
    navigate(pendente.href);
    bypassHistorico.current = false;
  };

  const persistir = async (): Promise<string | null> => {
    if (!form.nome_projeto.trim()) {
      toast({ title: 'Informe o nome do projeto', variant: 'destructive' });
      return null;
    }
    if (form.grupos.some((grupo) => !grupo.nome.trim())) {
      toast({ title: 'Informe o nome de todos os grupos de despesa', variant: 'destructive' });
      return null;
    }
    const semDescricao = form.itens.some((item) => !item.descricao.trim());
    if (semDescricao) {
      toast({ title: 'Informe a descrição de todos os itens', variant: 'destructive' });
      return null;
    }
    const payload = {
      nome_projeto: form.nome_projeto,
      cliente: form.cliente,
      local_obra: form.local_obra,
      observacoes: form.observacoes,
      grupos: form.grupos.map((grupo) => ({ ...grupo, nome: grupo.nome.trim() })),
      itens: form.itens,
    };
    const salvo = orcamentoId
      ? await orcamentosCarregadoresApi.salvar(orcamentoId, payload)
      : await orcamentosCarregadoresApi.criar(payload);
    const atualizado = formularioDeOrcamento(salvo);
    setOrcamentoId(salvo.id);
    setForm(atualizado);
    setSnapshot(JSON.stringify(atualizado));
    return salvo.id;
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const id = await persistir();
      if (id) toast({ title: 'Orçamento salvo' });
    } catch (e) {
      toast({
        title: 'Erro ao salvar',
        description: e instanceof Error ? e.message : 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setSalvando(false);
    }
  };

  const baixarPdf = async (id: string, nomeProjeto: string) => {
    const blob = await orcamentosCarregadoresApi.baixarPdf(id);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const nome = nomeProjeto
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 60);
    link.href = url;
    link.download = `Lista_materiais_${nome || 'orcamento'}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const gerarPdfDaLista = async (id: string, nomeProjeto: string) => {
    setGerandoPdfId(id);
    try {
      await baixarPdf(id, nomeProjeto);
    } catch (e) {
      toast({
        title: 'Erro ao gerar PDF',
        description: e instanceof Error ? e.message : 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setGerandoPdfId(null);
    }
  };

  const gerarPdfDoEditor = async () => {
    if (form.itens.length === 0) {
      toast({ title: 'Adicione ao menos um produto para gerar o PDF', variant: 'destructive' });
      return;
    }
    if (!form.itens.some(itemEntraNoPdf)) {
      toast({ title: 'Marque ao menos um item para incluir no PDF', variant: 'destructive' });
      return;
    }
    setGerandoPdfId('editor');
    try {
      const id = !orcamentoId || sujo ? await persistir() : orcamentoId;
      if (!id) return;
      await baixarPdf(id, form.nome_projeto);
    } catch (e) {
      toast({
        title: 'Erro ao gerar PDF',
        description: e instanceof Error ? e.message : 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setGerandoPdfId(null);
    }
  };

  const confirmarExclusao = async () => {
    if (!excluirId) return;
    setExcluindo(true);
    try {
      await orcamentosCarregadoresApi.excluir(excluirId);
      if (orcamentoId === excluirId) {
        setModo('lista');
        setOrcamentoId(null);
      }
      setExcluirId(null);
      toast({ title: 'Orçamento excluído' });
      carregarLista();
    } catch (e) {
      toast({
        title: 'Erro ao excluir',
        description: e instanceof Error ? e.message : 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setExcluindo(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {modo === 'lista' ? (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Orçamentos de obras</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Planilha de despesas da obra, separada por categoria.
              </p>
            </div>
            <Button onClick={abrirNovo}>
              <Plus className="h-4 w-4 mr-1" /> Novo orçamento
            </Button>
          </div>

          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Buscar por projeto, cliente ou local"
                  value={buscaLista}
                  onChange={(e) => setBuscaLista(e.target.value)}
                />
              </div>

              {carregandoLista ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : listaFiltrada.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10">
                  {lista.length === 0
                    ? 'Nenhum orçamento salvo. Crie o primeiro para uma obra.'
                    : 'Nenhum orçamento encontrado para essa busca.'}
                </p>
              ) : (
                <div className="border rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Projeto</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Local da obra</TableHead>
                        <TableHead className="text-right">Itens</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead>Atualizado</TableHead>
                        <TableHead className="w-[148px]" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {listaFiltrada.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <button
                              type="button"
                              className="font-medium text-left hover:underline"
                              onClick={() => abrirExistente(item.id)}
                            >
                              {item.nome_projeto}
                            </button>
                          </TableCell>
                          <TableCell>{item.cliente || '—'}</TableCell>
                          <TableCell>{item.local_obra || '—'}</TableCell>
                          <TableCell className="text-right">{item.qtd_itens}</TableCell>
                          <TableCell className="text-right">{formatarMoedaOrcamento(item.valor_total)}</TableCell>
                          <TableCell className="whitespace-nowrap">{formatarData(item.updated_at)}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Copiar para um novo orçamento"
                              aria-label={`Copiar ${item.nome_projeto} para um novo orçamento`}
                              disabled={carregandoEditor}
                              onClick={() => copiarExistente(item.id)}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`PDF de ${item.nome_projeto}`}
                              disabled={gerandoPdfId === item.id || item.qtd_itens === 0}
                              onClick={() => gerarPdfDaLista(item.id, item.nome_projeto)}
                            >
                              {gerandoPdfId === item.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <FileDown className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Excluir ${item.nome_projeto}`}
                              onClick={() => setExcluirId(item.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="space-y-1">
              <Button variant="ghost" className="px-0 h-8" onClick={voltar}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Voltar para a lista
              </Button>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                {orcamentoId ? 'Editar orçamento' : 'Novo orçamento'}
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {sujo && <span className="text-sm text-amber-700">Alterações não salvas</span>}
              {orcamentoId && (
                <Button variant="outline" onClick={() => setExcluirId(orcamentoId)} disabled={carregandoEditor}>
                  <Trash2 className="h-4 w-4 mr-1" /> Excluir
                </Button>
              )}
              {orcamentoId && (
                <Button variant="outline" onClick={copiarAtual} disabled={carregandoEditor || salvando}>
                  <Copy className="h-4 w-4 mr-1" /> Copiar para novo
                </Button>
              )}
              <Button
                variant="outline"
                onClick={gerarPdfDoEditor}
                disabled={carregandoEditor || salvando || gerandoPdfId === 'editor'}
              >
                {gerandoPdfId === 'editor' ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4 mr-1" />
                )}
                PDF dos materiais
              </Button>
              <Button onClick={salvar} disabled={salvando || carregandoEditor || gerandoPdfId === 'editor'}>
                {salvando ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Salvar orçamento
              </Button>
            </div>
          </div>

          {carregandoEditor ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1 md:col-span-1">
                    <Label htmlFor="nome-projeto">Nome do projeto</Label>
                    <Input
                      id="nome-projeto"
                      value={form.nome_projeto}
                      placeholder="Ex.: Condomínio Aurora — 8 vagas"
                      onChange={(e) => setForm((atual) => ({ ...atual, nome_projeto: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="cliente-obra">Cliente</Label>
                    <Input
                      id="cliente-obra"
                      value={form.cliente}
                      onChange={(e) => setForm((atual) => ({ ...atual, cliente: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="local-obra">Local da obra</Label>
                    <Input
                      id="local-obra"
                      value={form.local_obra}
                      onChange={(e) => setForm((atual) => ({ ...atual, local_obra: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1 md:col-span-3">
                    <Label htmlFor="observacoes-obra">Observações</Label>
                    <Textarea
                      id="observacoes-obra"
                      rows={2}
                      value={form.observacoes}
                      onChange={(e) => setForm((atual) => ({ ...atual, observacoes: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-1">
                      <h2 className="text-base font-semibold">Grupos de despesa</h2>
                      <p className="text-xs text-muted-foreground">
                        Separe a obra por categoria, como cabeamentos, eletrocalhas ou sistema de detecção de incêndio.
                        A descrição dos produtos cadastrados vem de Notas Fiscais.
                      </p>
                    </div>
                    <Button type="button" variant="outline" onClick={adicionarGrupo}>
                      <FolderPlus className="mr-1 h-4 w-4" /> Novo grupo
                    </Button>
                  </div>

                  {form.grupos.length === 0 ? (
                    <p className="rounded-md border py-8 text-center text-sm text-muted-foreground">
                      Nenhum grupo ainda. Crie o primeiro para lançar as despesas da obra.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          className="pl-9"
                          placeholder="Buscar material por código ou descrição"
                          value={buscaItens}
                          onChange={(e) => setBuscaItens(e.target.value)}
                          aria-label="Buscar material na lista"
                        />
                      </div>
                      <DragDropContext onDragEnd={aoSoltarItem}>
                        <div className="space-y-4">
                          {form.grupos.map((grupo) => (
                            <SecaoGrupo
                              key={grupo.id}
                              grupo={grupo}
                              itens={form.itens.filter((item) => item.grupo_id === grupo.id)}
                              produtos={produtosOrdenados}
                              produtoSelecionado={produtoPorGrupo[grupo.id] || ''}
                              termo={termoItens}
                              onProduto={(id) => setProdutoPorGrupo((atual) => ({ ...atual, [grupo.id]: id }))}
                              onAdicionarProduto={() => adicionarProdutoNoGrupo(grupo.id)}
                              onAdicionarAvulso={() => adicionarAvulsoNoGrupo(grupo.id)}
                              onRenomear={(nome) => renomearGrupo(grupo.id, nome)}
                              onExcluir={() => pedirExclusaoGrupo(grupo.id)}
                              onAtualizarItem={atualizarItem}
                              onRemoverItem={removerItem}
                              onMarcarPdf={(marcado) => marcarPdfDoGrupo(grupo.id, marcado)}
                            />
                          ))}
                        </div>
                      </DragDropContext>
                      <p className="text-xs text-muted-foreground">
                        Arraste o ícone à esquerda para reordenar ou mover o item para outro grupo. Desmarque PDF nos itens que não devem sair no documento.
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2 rounded-md border bg-muted/40 px-4 py-3">
                  {form.grupos.map((grupo) => (
                    <div key={grupo.id} className="flex items-center justify-between gap-3 text-sm">
                      <span className="truncate text-muted-foreground">{grupo.nome.trim() || 'Grupo sem nome'}</span>
                      <span>{formatarMoedaOrcamento(totalGrupoOrcamento(form.itens, grupo.id))}</span>
                    </div>
                  ))}
                  <div
                    className={cn(
                      'flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between',
                      form.grupos.length > 0 && 'border-t pt-2'
                    )}
                  >
                    <span className="text-sm text-muted-foreground">
                      {form.itens.length} {form.itens.length === 1 ? 'item' : 'itens'}
                      {form.itens.length > 0 ? ` · ${incluidosNoPdf} no PDF` : ''}
                    </span>
                    <span className="text-lg font-semibold">Total geral {formatarMoedaOrcamento(total)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <AlertDialog open={Boolean(excluirId)} onOpenChange={(aberto) => !aberto && !excluindo && setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir orçamento?</AlertDialogTitle>
            <AlertDialogDescription>
              O projeto e a planilha de itens serão removidos. Essa ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={excluindo}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarExclusao} disabled={excluindo}>
              {excluindo ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(excluirGrupoId)} onOpenChange={(aberto) => !aberto && setExcluirGrupoId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir grupo de despesa?</AlertDialogTitle>
            <AlertDialogDescription>
              {form.grupos.find((grupo) => grupo.id === excluirGrupoId)?.nome.trim() || 'Este grupo'} e os itens
              dele serão removidos do orçamento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarExclusaoGrupo}>Excluir grupo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(saidaPendente)} onOpenChange={(aberto) => !aberto && setSaidaPendente(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sair sem salvar?</AlertDialogTitle>
            <AlertDialogDescription>
              Há alterações não salvas neste orçamento. Se você sair agora, essas alterações serão perdidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarSaida}>Sair sem salvar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
